import {
  isReferenceBlock,
  ReferenceBlock,
  InstanceBlock,
  ComponentBlock,
  Block,
  isReferenceableBlock,
  BlockType,
  getBlockUpdateOverride,
  VariantData,
  getValueBasedOnSelectedVariants,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { CreateBlockParams, createBlock, updateBlock } from './crud'
import { getIdempotentId } from '@basehub/utils/id/index'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { WithServerSecret } from '@basehub/data/replicache/tx/spec'
import { WriteTransaction } from 'replicache'
import { doCopyOnWrite, idToSourceBlockId } from './copy-on-write'
import { BlockBatchMutationParams } from './batch'
import { ensureBaseHubTransaction } from '@basehub/data/replicache/tx/iso'
import { PerfReport } from '@basehub/utils/perf/index'

/* -------------------------------------------------------------------------------------------------
 * Detach Blocks from Target (by some other block).
   When a component is deleted, we detach it from the blocks that target it.
 * -----------------------------------------------------------------------------------------------*/
const DEBUG = false
export async function detachBlocksFromTarget(
  tx: WriteTransaction,
  {
    sourceBlockId,
    rootBlockId,
    isoNow,
    perfReport,
  }: {
    sourceBlockId: string
    rootBlockId: string
    isoNow: string
    perfReport?: PerfReport
  }
) {
  ensureBaseHubTransaction(tx)

  if (DEBUG) console.log({ existingTm: tx.currentTreeManager })
  const treeManager = await tx.getTreeManager(rootBlockId)

  perfReport?.checkpoint('detachBlocksFromTarget:GET_TM')

  const undo: BlockBatchMutationParams['mutations'] = []

  const blocksByType = (treeManager.compoundIndexes.get('blocksByType') ??
    new Map()) as Map<BlockType, Set<string>>
  const references = blocksByType.get('reference') ?? new Set()
  if (DEBUG) console.log({ references, sourceBlockId })

  for (const id of references) {
    const referenceOrUnionBlock = treeManager.getNode(id)?.value

    if (
      // @ts-ignore
      referenceOrUnionBlock?.__virtual
    ) {
      continue
    }

    if (!referenceOrUnionBlock) continue

    invariant(isReferenceBlock(referenceOrUnionBlock), 'Invalid block')

    const referenceOrUnionBlockPath = treeManager.getPath(
      referenceOrUnionBlock.id,
      { format: 'string' }
    )

    if (!referenceOrUnionBlockPath) continue

    const updates: Partial<ReferenceBlock> = {}
    let hasChanged = false
    if (isReferenceBlock(referenceOrUnionBlock)) {
      const indexOfBlockInReferenceBlock =
        referenceOrUnionBlock.value.targetBlockIds.indexOf(sourceBlockId)
      const blockExistsInReferenceBlock = indexOfBlockInReferenceBlock !== -1

      if (blockExistsInReferenceBlock) {
        const filtered = referenceOrUnionBlock.value.targetBlockIds.filter(
          (t) => t !== sourceBlockId
        )
        // remove targetBlockId from base value
        updates.value = {
          ...referenceOrUnionBlock.value,
          targetBlockIds: filtered,
        }
        hasChanged = true
      }

      // Also detach from variant overrides that target the source block
      const existingVariantOverrides = referenceOrUnionBlock.variantOverrides
      if (existingVariantOverrides) {
        let variantOverridesChanged = false
        const nextVariantOverrides: NonNullable<
          ReferenceBlock['variantOverrides']
        > = { ...existingVariantOverrides }

        for (const [overrideKey, overrideValue] of Object.entries(
          existingVariantOverrides
        )) {
          const currentTargetIds = overrideValue?.value?.targetBlockIds
          if (
            Array.isArray(currentTargetIds) &&
            currentTargetIds.includes(sourceBlockId)
          ) {
            const filteredIds = currentTargetIds.filter(
              (t) => t !== sourceBlockId
            )

            nextVariantOverrides[overrideKey] = {
              ...overrideValue,
              value: {
                ...(overrideValue?.value ?? {}),
                targetBlockIds: filteredIds,
              },
            }
            variantOverridesChanged = true
          }
        }

        if (variantOverridesChanged) {
          updates.variantOverrides = nextVariantOverrides
          hasChanged = true
        }
      }
    }

    if (referenceOrUnionBlock.constraints.acceptedTypes) {
      const filtered = referenceOrUnionBlock.constraints.acceptedTypes.filter(
        (t) => t !== sourceBlockId
      )
      if (
        filtered.length !==
        referenceOrUnionBlock.constraints.acceptedTypes.length
      ) {
        // has changed
        updates.constraints = {
          ...referenceOrUnionBlock.constraints,
          acceptedTypes: filtered,
        }
        hasChanged = true
      }
    }

    if (hasChanged) {
      if (DEBUG) console.log({ referenceOrUnionBlock, treeManager })
      const { undo: undoForThis, updatedBlock } = await updateBlock(tx, {
        id: referenceOrUnionBlock.id,
        path: referenceOrUnionBlockPath,
        instanceChildTarget: referenceOrUnionBlock.instanceChildTarget,
        isoNow,
        strategy: 'merge',
        data: {
          type: referenceOrUnionBlock.type,
          ...(updates as any),
        },
      })
      treeManager.updateNode(updatedBlock.id, {
        value: updatedBlock,
      })
      undo.unshift(...undoForThis)
    }
  }

  perfReport?.checkpoint('detachBlocksFromTarget:DONE')

  return { undo }
}

/* -------------------------------------------------------------------------------------------------
 * CREATE, AND THEN REFERENCE A BLOCK
 * -----------------------------------------------------------------------------------------------*/

export type CreateAndReferenceBlockParams = {
  newBlock: CreateBlockParams & {
    data: Extract<CreateBlockParams['data'], { type: 'instance' | 'component' }>
  }
  placeNewBlockBelowPath?: string
  referenceBlockId: ReferenceBlock['id']
  referenceBlockInstanceChildTarget: ReferenceBlock['instanceChildTarget']
  multiple: boolean
  path: string
  idSeed: string
  isoNow: string
  selectedDashboardVariants: VariantData[]
}

export const createAndReferenceBlock = async (
  tx: WriteTransaction,
  {
    newBlock,
    referenceBlockId: _referenceBlockId,
    referenceBlockInstanceChildTarget,
    multiple,
    path: _path,
    placeNewBlockBelowPath,
    idSeed,
    isoNow,
    selectedDashboardVariants,
  }: WithServerSecret<CreateAndReferenceBlockParams>
): Promise<{
  newBlock: InstanceBlock | ComponentBlock
  parentBlockSourceId: Block['id']
  undo: BlockBatchMutationParams['mutations']
}> => {
  ensureBaseHubTransaction(tx)

  const undo: BlockBatchMutationParams['mutations'] = []

  const { block: referenceBlock, path } = await doCopyOnWrite(tx, {
    isoNow,
    partialBlock: {
      id: _referenceBlockId,
      instanceChildTarget: referenceBlockInstanceChildTarget,
    },
    path: _path,
  })
  invariant(
    isReferenceBlock(referenceBlock),
    `Block should be reference, found ${referenceBlock.type}`
  )

  try {
    const currentTargetBlockIds = getValueBasedOnSelectedVariants({
      block: referenceBlock,
      variantsToFilterBy: selectedDashboardVariants,
    }).targetBlockIds

    const newBlockId = (idSeed = getIdempotentId({ idSeed }))
    const { undo: undoRefOp } = await updateBlock(tx, {
      id: referenceBlock.id,
      data: getBlockUpdateOverride({
        block: referenceBlock,
        newValue: {
          targetBlockIds: multiple
            ? [...currentTargetBlockIds, newBlockId]
            : [newBlockId],
        },
        selectedVariants: selectedDashboardVariants,
      }),
      instanceChildTarget: referenceBlock.instanceChildTarget,
      strategy: 'merge-deep',
      path,
      isoNow,
      ignoreHashComparison: true,
    })

    undo.push(...undoRefOp)

    const {
      newBlock: block,
      undo: undoForThis,
      parentBlock,
    } = await createBlock(tx, {
      ...newBlock,
      path: placeNewBlockBelowPath ?? path,
      data: {
        ...newBlock.data,
        id: newBlockId,
      },
      skipInstanceValidation: true,
    })

    undo.push(...undoForThis)

    invariant(
      isReferenceableBlock(block),
      'Referenced block should be either instance or component'
    )
    invariant(idToSourceBlockId(block) === newBlockId, `block id mismatch`)

    return {
      newBlock: block,
      parentBlockSourceId: idToSourceBlockId(parentBlock),
      undo,
    }
  } catch (err) {
    if (err instanceof MutatorUserError) {
      throw new MutatorUserError(err.reason)
    } else {
      console.error(err)
      throw new Error('An error occurred')
    }
  }
}
